import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { isMobile } from 'react-device-detect';
import { useQuery } from '@tanstack/react-query';

import useCompanyInfo from '@/hooks/useCompanyInfo';
import { getMonthlyBills } from '@/apis/company';
import { QUERY_KEY } from '@/constants/queryKey';

import { logout } from '@/utils/logout';

import { ROUTER } from '@/constants/constants';

import NoticePopup from '@/components/common/NoticePopup';

import { useAccessTokenStore } from '@/store/auth';
import { useLoadingStore } from '@/store/loading';

import { LoadingBackground } from '@/styles/common';
import { Theme } from '@/styles/Theme';
import PropagateLoader from 'react-spinners/PropagateLoader';

const UserRoute = () => {
  const { accessToken } = useAccessTokenStore();
  const { loading } = useLoadingStore();
  const { data: companyInfo, isLoading } = useCompanyInfo();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Fetch failed bills for PREPAID expired users
  const { data: prepaidFailedBills } = useQuery(
    [QUERY_KEY.MONTHLY_BILLS, { status: 'failed', payment_method: 'prepaid' }],
    () => getMonthlyBills({ status: 'failed', payment_method: 'prepaid' }),
    {
      enabled:
        !!companyInfo?.data &&
        companyInfo.data.subscriptionStatus === 'expired' &&
        companyInfo.data.paymentMethod === 'prepaid',
      staleTime: 5 * 60 * 1000,
    }
  );

  // Fetch overdue bills for POSTPAID expired users
  const { data: postpaidOverdueBills } = useQuery(
    [QUERY_KEY.MONTHLY_BILLS, { status: 'overdue', payment_method: 'postpaid' }],
    () => getMonthlyBills({ status: 'overdue', payment_method: 'postpaid' }),
    {
      enabled:
        !!companyInfo?.data &&
        companyInfo.data.subscriptionStatus === 'expired' &&
        companyInfo.data.paymentMethod === 'postpaid',
      staleTime: 5 * 60 * 1000,
    }
  );

  // Fetch sent bills for POSTPAID expired users (fallback if no overdue)
  const { data: postpaidSentBills } = useQuery(
    [QUERY_KEY.MONTHLY_BILLS, { status: 'sent', payment_method: 'postpaid' }],
    () => getMonthlyBills({ status: 'sent', payment_method: 'postpaid' }),
    {
      enabled:
        !!companyInfo?.data &&
        companyInfo.data.subscriptionStatus === 'expired' &&
        companyInfo.data.paymentMethod === 'postpaid',
      staleTime: 5 * 60 * 1000,
    }
  );

  useEffect(() => {
    if (!accessToken) {
      navigate(ROUTER.LOGIN, {
        replace: true,
      });
      toast.error('로그인이 필요합니다.');
      return;
    }

    if (isMobile) {
      logout('모바일에서 사용하실 수 없습니다.');
      return;
    }

    if (companyInfo) {
      const {
        data: { subscriptionStatus, paymentMethod },
      } = companyInfo;
      
      // Expired users: redirect to payment screen
      if (subscriptionStatus === 'expired') {
        // Check if already on payment bill page
        const isOnPaymentBillPage = pathname.startsWith(ROUTER.PAYMENT_BILL);
        
        if (!isOnPaymentBillPage) {
          // PREPAID: Redirect to failed bill payment page
          if (paymentMethod === 'prepaid') {
            const failedBills = prepaidFailedBills?.data?.bills || [];
        if (failedBills.length > 0) {
          // Redirect to the most recent failed bill
          const mostRecentBill = failedBills[0];
          navigate(`${ROUTER.PAYMENT_BILL}/${mostRecentBill.id}`, { replace: true });
          return;
        }
        // Fallback: Redirect to card register if no failed bill found
        navigate(ROUTER.CARD_REGISTER, { replace: true });
        return;
          }
          
          // POSTPAID: Redirect to overdue/sent bill payment confirmation page
          if (paymentMethod === 'postpaid') {
            // Priority: overdue bills first, then sent bills
            const overdueBills = postpaidOverdueBills?.data?.bills || [];
            const sentBills = postpaidSentBills?.data?.bills || [];
            
            if (overdueBills.length > 0) {
              // Redirect to the most recent overdue bill
              const mostRecentBill = overdueBills[0];
              navigate(`${ROUTER.PAYMENT_BILL}/${mostRecentBill.id}`, { replace: true });
              return;
            } else if (sentBills.length > 0) {
              // Redirect to the most recent sent bill
              const mostRecentBill = sentBills[0];
              navigate(`${ROUTER.PAYMENT_BILL}/${mostRecentBill.id}`, { replace: true });
              return;
            }
            // Fallback: Redirect to card register if no bill found
            navigate(ROUTER.CARD_REGISTER, { replace: true });
            return;
          }
          
          // Unknown payment method: Fallback to card register
          navigate(ROUTER.CARD_REGISTER, { replace: true });
          return;
        }
      }
      // REMOVED: isApproved check (replaced by subscription_status = 'expired')
      // All new users now default to isApproved = 1 and subscription_status = 'free'
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, companyInfo, pathname, prepaidFailedBills, postpaidOverdueBills, postpaidSentBills]);

  if (isLoading || !companyInfo) {
    return null;
  }

  return (
    <>
      {loading && (
        <LoadingBackground>
          <PropagateLoader color={Theme.colors.B_3} />
        </LoadingBackground>
      )}
      {companyInfo.data.isApproved && <NoticePopup />}
      <Outlet />
    </>
  );
};

export default UserRoute;
